import React, { useEffect, useState } from "react";
import { api, setSessionToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, LogOut, Shield, Store, Wallet, TrendingUp, Users, Percent } from "lucide-react";
import { Button } from "../components/ui/button";

const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

export default function SubAdminDashboard() {
  const { user, logout } = useAuth() || {};
  const [dash, setDash] = useState(null);
  const [comm, setComm] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get("/sub-admin/me/dashboard").then((r) => r.data),
      api.get("/sub-admin/me/commissions").then((r) => r.data),
      api.get("/sub-admin/me/shops").then((r) => r.data),
    ]).then(([d, c, s]) => { setDash(d); setComm(c); setShops(s); })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !dash) return <div className="min-h-screen grid place-items-center bg-slate-950"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  const perms = dash.permissions || {};
  const showKPI = (m) => perms[m] !== false;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="sub-admin-dashboard">
      <header className="border-b border-slate-800 bg-[#0F131C] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2"><Shield className="w-6 h-6 text-emerald-400" /><div className="font-display text-lg font-bold text-white">Sub Admin — {user?.email}</div></div>
        <Button variant="outline" onClick={() => { logout?.(); setSessionToken(null); window.location.href = "/login"; }} className="border-slate-700 text-slate-200" data-testid="subadmin-logout"><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {showKPI("shops") && <Card icon={Store} label="Assigned Shops" value={dash.assigned_shops} tone="sky" testid="kpi-assigned" />}
          {showKPI("shops") && <Card icon={Users} label="New Shops (30d)" value={dash.new_shops} tone="emerald" testid="kpi-new" />}
          {showKPI("shops") && <Card icon={Store} label="Active Shops" value={dash.active_shops ?? dash.paid_shops} tone="emerald" testid="kpi-active" />}
          {showKPI("shops") && <Card icon={Users} label="Pending Payments" value={dash.pending_payments ?? 0} tone="amber" testid="kpi-pending-payments" />}
          {showKPI("subscriptions") && <Card icon={Store} label="Monthly Subs" value={dash.monthly_shops ?? 0} tone="emerald" testid="kpi-monthly" />}
          {showKPI("subscriptions") && <Card icon={Store} label="Yearly Subs" value={dash.yearly_shops ?? 0} tone="sky" testid="kpi-yearly" />}
          {showKPI("commissions") && <Card icon={TrendingUp} label="Total Revenue" value={fmtPKR(dash.total_revenue)} tone="slate" testid="kpi-revenue" />}
          {showKPI("commissions") && <Card icon={Wallet} label="Pending Commission" value={fmtPKR(dash.commission_pending)} tone="amber" testid="kpi-pending" />}
          {showKPI("commissions") && <Card icon={Wallet} label="Paid Commission" value={fmtPKR(dash.commission_paid)} tone="emerald" testid="kpi-paid-comm" />}
          {showKPI("commissions") && <Card icon={Percent} label="Commission Rate" value={`${dash.commission_rate}%`} tone="sky" testid="kpi-rate" />}
        </div>

        {showKPI("shops") && (
          <section className="rounded-md border border-slate-800 bg-[#0F131C] p-4">
            <h2 className="font-display text-lg font-bold text-white mb-3 flex items-center gap-2"><Store className="w-5 h-5" /> Assigned Shops ({shops.length})</h2>
            {shops.length === 0 ? <div className="text-sm text-slate-500 py-3">No shops assigned yet. Contact Super Admin.</div> :
              <div className="divide-y divide-slate-800">
                {shops.slice(0, 20).map((s) => (
                  <div key={s.id} className="py-2.5 flex items-center justify-between" data-testid={`subadmin-shop-${s.id}`}>
                    <div><div className="text-white font-medium">{s.name}</div><div className="text-xs text-slate-500">{s.city || "—"} · Trial ends {(s.trial_ends || "").slice(0, 10)}</div></div>
                    <div className="text-xs text-slate-400"><b>{s.plan_id}</b> · {s.status}</div>
                  </div>
                ))}
              </div>}
          </section>
        )}

        {showKPI("commissions") && comm && (
          <section className="rounded-md border border-slate-800 bg-[#0F131C] p-4" data-testid="subadmin-my-commission">
            <h2 className="font-display text-lg font-bold text-white mb-3 flex items-center gap-2"><Wallet className="w-5 h-5" /> My Commission</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <MiniStat label="Rate" value={`${comm.commission_rate}%`} />
              <MiniStat label="Total" value={fmtPKR(comm.total_commission)} />
              <MiniStat label="Paid" value={fmtPKR(comm.paid_commission)} />
              <MiniStat label="Pending" value={fmtPKR(comm.pending_commission)} />
            </div>
            <div className="text-xs text-slate-500 mb-2">Commission history ({comm.history.length})</div>
            {comm.history.length === 0 ? <div className="text-sm text-slate-500 py-3">No commissions yet.</div> :
              <div className="divide-y divide-slate-800 max-h-96 overflow-y-auto">
                {comm.history.map((h) => (
                  <div key={h.id} className="py-2.5 flex justify-between text-sm">
                    <div><div className="text-white">{h.shop_name} · <span className="uppercase text-xs">{h.plan_id}</span></div><div className="text-xs text-slate-500">{(h.created_at || "").slice(0, 10)} · {h.commission_rate}% of {fmtPKR(h.net_revenue)}</div></div>
                    <div className="text-right"><div className={h.status === "paid" ? "text-emerald-400" : "text-amber-400"}>{fmtPKR(h.commission_amount)}</div><div className="text-[10px] uppercase text-slate-500">{h.status}</div></div>
                  </div>
                ))}
              </div>}
          </section>
        )}
      </main>
    </div>
  );
}

function Card({ icon: Icon, label, value, tone, testid }) {
  const map = { slate: "border-slate-700 bg-slate-900", sky: "border-sky-800 bg-sky-950/30", amber: "border-amber-800 bg-amber-950/30", emerald: "border-emerald-800 bg-emerald-950/30" };
  return (
    <div className={`rounded-md border p-3 ${map[tone] || map.slate}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-[10px] uppercase tracking-widest font-bold"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="font-display text-xl font-bold text-white mt-1">{value}</div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return <div className="rounded-md border border-slate-800 bg-slate-900 p-2.5"><div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</div><div className="text-white font-display font-bold mt-0.5">{value}</div></div>;
}
